import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, HStack, VStack, Text, Button, useDisclosure, Grid, Icon, Skeleton } from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineArrowRight } from "react-icons/hi2";
import axios from "axios";
import Header from "./Header";
import TeacherClassroomsTile from "./TeacherClassroomsTile";
import ClassroomStrip from "./dashboard/ClassroomStrip";
import WeekPulse from "./dashboard/WeekPulse";
import { StateColumns, StateLegend } from "./marking/Charts";
import { stageOf } from "./marking/markingTokens";
import ClassroomCard from "./ClassroomCard";
import CreateClassroom from "./CreateClassroom";
import { logout } from "../Components/auth/logout";
import { BASE_API_URL } from "../Components/config";
import { c, useClassroomColorMode } from "./classroomColors";
import { brand } from "./standeTokens";
import { useClassroomToast } from "./classroomToast";
import "./classroom-theme.css";

const API = `${BASE_API_URL}/api/v1`;

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const DashboardTeacher = () => {
  const { isDark, toggleColorMode } = useClassroomColorMode();
  const toast = useClassroomToast(isDark);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // null = still loading; [] = loaded, none.
  const [classrooms, setClassrooms] = useState(null);
  const [classroomsError, setClassroomsError] = useState(null);
  const token = `Bearer ${localStorage.getItem("accessToken")}`;
  const handleLogout = () => logout({ navigate });

  const fetchClassrooms = useCallback(() => {
    return axios
      .get(`${API}/user-course-links/detail/?user_type=INSTRUCTOR&limit=100`, { headers: { Authorization: token } })
      .then((response) => {
        setClassrooms(response.data);
        setClassroomsError(null);
      })
      .catch((err) => {
        console.error(err);
        setClassrooms([]);
        setClassroomsError("There was an error loading your spaces.");
      });
  }, [token]);

  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  // One request covers every classroom: the endpoint returns an instructor's
  // attempts across all the courses they are linked to when no course is named.
  const [attempts, setAttempts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/user-course-links/user-paper-attempts/detail/?course_user_type=INSTRUCTOR&limit=300`,
        { headers: { Authorization: token } })
      .then((res) => { if (!cancelled) setAttempts(res.data || []); })
      .catch((err) => { if (!cancelled) { console.error(err); setAttempts([]); } });
    return () => { cancelled = true; };
  }, [token]);

  // Per-classroom marking state, keyed by course.
  const byCourse = useMemo(() => {
    const map = new Map();
    (attempts || []).forEach((r) => {
      if (!r.course_guid) return;
      if (!map.has(r.course_guid)) {
        map.set(r.course_guid, {
          counts: { not_submitted: 0, to_review: 0, marked: 0, released: 0 },
          total: 0,
        });
      }
      const e = map.get(r.course_guid);
      e.counts[stageOf(r)] += 1;
      e.total += 1;
    });
    return map;
  }, [attempts]);

  // The chart takes the same shape whether its columns are assignments or
  // classrooms, so the dashboard just builds classroom-shaped items.
  const columnItems = useMemo(() => {
    if (!attempts) return [];
    return (classrooms || [])
      .map((cls) => {
        const e = byCourse.get(cls.course_guid);
        if (!e || !e.total) return null;
        return {
          key: cls.course_guid,
          name: cls.course_name,
          counts: e.counts,
          total: e.total,
          submitted: e.total - e.counts.not_submitted,
          needsReview: e.counts.to_review,
          cls,
        };
      })
      .filter(Boolean)
      .sort((x, y) => y.needsReview - x.needsReview || y.total - x.total);
  }, [classrooms, byCourse, attempts]);

  const waiting = useMemo(
    () => (attempts || []).filter((r) => stageOf(r) === "to_review").length,
    [attempts]
  );
  const totals = useMemo(() => {
    const counts = { not_submitted: 0, to_review: 0, marked: 0, released: 0 };
    (attempts || []).forEach((r) => { counts[stageOf(r)] += 1; });
    return { counts };
  }, [attempts]);

  const classroomsWaiting = useMemo(
    () => [...byCourse.values()].filter((e) => e.counts.to_review > 0).length,
    [byCourse]
  );

  const openClassroom = (cls) => {
    navigate("/classroom/teacher/class", {
      state: { courseGuid: cls.course_guid, classroomName: cls.course_name, userType: "INSTRUCTOR" },
    });
  };

  // The classroom is hidden rather than destroyed (the backend keeps the
  // students' work), so this is safe to offer from the card.
  const deleteClassroom = async (cls) => {
    try {
      await axios.delete(`${API}/courses/${cls.course_guid}`, { headers: { Authorization: token } });
      setClassrooms((prev) => (prev || []).filter((x) => x.course_guid !== cls.course_guid));
      toast({ title: "Space deleted", description: `${cls.course_name} is gone from your list.`, status: "success" });
    } catch (err) {
      console.error(err);
      toast({ title: "Couldn't delete the space", description: "Please try again.", status: "error" });
      throw err;
    }
  };

  const loadedClassrooms = classrooms || [];

  return (
    <Box className="stande-classroom" data-theme={isDark ? "dark" : "light"} minHeight="100vh" bg={c.page} transition="background 0.2s">
      <Box paddingLeft="75px" paddingRight="75px">
        <Header handleLogout={handleLogout} isDark={isDark} onToggleColorMode={toggleColorMode} />
      </Box>

      <Box padding="48px 75px">
        <Flex justifyContent="space-between" alignItems="flex-end" mb="40px">
          <VStack align="flex-start" spacing="4px">
            <Text fontSize="28px" fontWeight="500" color={c.text}>
              {greeting()}
            </Text>
            <Text fontSize="16px" color={c.textSub} fontWeight="300">
              Welcome back! Here's an overview of your spaces and assignments.
            </Text>
          </VStack>
        </Flex>

        {/* Triage: what needs this teacher, across every classroom. The work
            itself happens in a classroom's Marking tab. */}
        <Flex gap="18px" direction={{ base: "column", lg: "row" }} align="stretch" mb="30px">
          <Box
            flex="0 0 auto"
            width={{ base: "100%", lg: "300px" }}
            bg={c.surface}
            border="1px solid"
            borderColor={c.borderSoft}
            borderRadius="14px"
            p="20px"
            display="flex"
            flexDirection="column"
          >
            <Text fontSize="12px" color={c.textMuted} mb="7px">
              Waiting on you
            </Text>
            {attempts === null ? (
              <Skeleton h="40px" w="130px" borderRadius="8px" startColor={c.surfaceSubtle} endColor={c.hover} />
            ) : (
              <HStack spacing="9px" align="baseline">
                <Text fontSize="40px" fontWeight="500" lineHeight="1"
                  color={waiting ? brand(isDark).secondaryOrange : c.text}
                  sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {waiting}
                </Text>
                <Text fontSize="14px" fontWeight="300" color={c.textSub}>
                  {waiting === 1 ? "submission" : "submissions"}
                </Text>
              </HStack>
            )}
            <Text fontSize="13px" fontWeight="300" color={c.textSub} mt="8px">
              {attempts === null
                ? "\u00a0"
                : waiting === 0
                ? "Nothing to mark right now."
                : `Across ${classroomsWaiting} space${classroomsWaiting === 1 ? "" : "s"}.`}
            </Text>

            {waiting > 0 && (
              <Button
                rightIcon={<Icon as={HiOutlineArrowRight} boxSize="14px" />}
                size="sm" h="34px" px="14px" mt="16px" alignSelf="flex-start"
                borderRadius="9px" bg={c.solidBg} color={c.solidText}
                fontSize="13px" fontWeight="500" _hover={{ opacity: 0.88 }}
                onClick={() => {
                  const busiest = columnItems.find((i) => i.needsReview > 0);
                  if (busiest) openClassroom(busiest.cls);
                }}
              >
                Start marking
              </Button>
            )}

            {/* This week's flow, folded in rather than given a card of its own. */}
            <Box mt="auto" pt="18px">
              {attempts === null ? (
                <Skeleton h="44px" borderRadius="8px" startColor={c.surfaceSubtle} endColor={c.hover} />
              ) : (
                <WeekPulse rows={attempts} isDark={isDark} />
              )}
            </Box>
          </Box>

          <Box
            flex="1 1 auto"
            minW={0}
            maxW={{ base: "100%", lg: "660px" }}
            bg={c.surface}
            border="1px solid"
            borderColor={c.borderSoft}
            borderRadius="14px"
            p="20px"
          >
            <Flex justify="space-between" align="baseline" gap="12px" wrap="wrap" mb="6px">
              <Text fontSize="14px" fontWeight="600" color={c.text}>
                Where your spaces stand
              </Text>
              <Text fontSize="11.5px" color={c.textMuted}>
                Most in need of marking, highlighted
              </Text>
            </Flex>

            {attempts === null ? (
              <Skeleton h="210px" borderRadius="10px" startColor={c.surfaceSubtle} endColor={c.hover} />
            ) : columnItems.length === 0 ? (
              <Flex align="center" justify="center" height="210px">
                <Text fontSize="13px" color={c.textMuted} fontWeight="300">
                  No work set in any space yet.
                </Text>
              </Flex>
            ) : (
              <>
                <StateColumns
                  items={columnItems}
                  isDark={isDark}
                  plotHeight={170}
                  onSelect={(item) => openClassroom(item.cls)}
                />
                <Box mt="14px" pt="12px" borderTop="1px solid" borderColor={c.borderSoft}>
                  <StateLegend isDark={isDark} counts={totals.counts} />
                </Box>
              </>
            )}
          </Box>
        </Flex>

        <Box
          bg={c.surface}
          border="1px solid"
          borderColor={c.borderSoft}
          borderRadius="14px"
          p="22px"
        >
        <Flex justifyContent="space-between" align="center" mb="16px">
          <TeacherClassroomsTile label="My Spaces" />
          <Button
            leftIcon={<Icon as={HiOutlinePlus} />}
            size="sm"
            variant="outline"
            borderRadius="8px"
            onClick={onOpen}
            fontSize="12px"
            fontWeight="500"
            color={c.text}
            borderColor={c.inputBorder}
            _hover={{ bg: c.hover }}
          >
            Create
          </Button>
        </Flex>

        {classrooms === null ? (
          <Grid templateColumns="repeat(auto-fill, minmax(280px, 360px))" gap="16px" justifyContent="flex-start">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} h="160px" borderRadius="14px" startColor={c.surfaceSubtle} endColor={c.hover} />
            ))}
          </Grid>
        ) : loadedClassrooms.length === 0 ? (
          <Flex p="40px" bg={c.surfaceSubtle} border="1px solid" borderColor={c.inputBorder}
            borderRadius="14px" justify="center">
            <Text fontSize="14px" color={c.textSub} fontWeight="300">
              {classroomsError || "No spaces created yet."}
            </Text>
          </Flex>
        ) : (
          <Grid templateColumns="repeat(auto-fill, minmax(280px, 360px))" gap="16px" justifyContent="flex-start">
            {loadedClassrooms.map((cls) => {
              const stats = byCourse.get(cls.course_guid);
              return (
                <ClassroomCard
                  key={cls.course_guid}
                  subjectCode={cls.subject_code}
                  classroomName={cls.course_name}
                  qualificationCode={cls.exam_qualification_code}
                  boardName={cls.exam_board_code}
                  typeOfUser="INSTRUCTOR"
                  isDark={isDark}
                  onClick={() => openClassroom(cls)}
                  onDelete={() => deleteClassroom(cls)}
                  footer={
                    attempts === null ? null : (
                      <ClassroomStrip
                        counts={stats?.counts || { not_submitted: 0, to_review: 0, marked: 0, released: 0 }}
                        total={stats?.total || 0}
                        toReview={stats?.counts.to_review || 0}
                        isDark={isDark}
                      />
                    )
                  }
                />
              );
            })}
          </Grid>
        )}

        </Box>

        <CreateClassroom isOpen={isOpen} onClose={onClose} isDark={isDark} onCreated={fetchClassrooms} />
      </Box>
    </Box>
  );
};

export default DashboardTeacher;
